<template>
  <div id="printArea" class="a4-page">
    <!-- 顶部标题 -->
    <div class="page-header">
      <h2>教练档案</h2>
      <div class="detail-summary">
        <span class="summary-name">{{ displayName }}</span>
        <el-tag v-if="sexText" size="small" type="info">{{ sexText }}</el-tag>
        <el-tag v-if="hrStaffType !== '—'" size="small">{{ hrStaffType }}</el-tag>
      </div>
    </div>

    <!-- 基本信息（整宽） -->
    <div class="section-title">基本信息</div>
    <el-descriptions :column="2" border size="small" class="info-section">
      <el-descriptions-item label="姓名">{{ displayName }}</el-descriptions-item>
      <el-descriptions-item label="工号">{{ workNumber || '—' }}</el-descriptions-item>
      <el-descriptions-item label="工作电话">{{ formData.coachPhone || '—' }}</el-descriptions-item>
      <el-descriptions-item label="状态">{{ formData.state || '—' }}</el-descriptions-item>
      <el-descriptions-item label="所属部门">{{ deptName || '—' }}</el-descriptions-item>
      <el-descriptions-item label="所属校区">
        {{ campusName || '—' }}
        <template v-if="formData.flagName">（{{ formData.flagName }}）</template>
      </el-descriptions-item>
      <el-descriptions-item label="添加时间">{{ formData.addDate || '—' }}</el-descriptions-item>
      <el-descriptions-item label="是否可见">{{ formData.isVisible === 1 ? '是' : (formData.isVisible === 0 ? '否' : '—') }}</el-descriptions-item>
    </el-descriptions>

    <!-- 工作信息（整宽） -->
    <div class="section-title">工作信息</div>
    <el-descriptions :column="1" border size="small">
      <el-descriptions-item label="ID">{{ formData.coachId != null ? formData.coachId : '—' }}</el-descriptions-item>
      <el-descriptions-item label="工号">{{ workNumber || '—' }}</el-descriptions-item>
      <el-descriptions-item label="工作电话">{{ formData.coachPhone || '—' }}</el-descriptions-item>
      <el-descriptions-item label="所属部门">{{ deptName || '—' }}</el-descriptions-item>
      <el-descriptions-item label="所属校区">
        {{ campusName ? campusName + (formData.flagName ? '-' + formData.flagName : '') : '—' }}
      </el-descriptions-item>
      <el-descriptions-item label="拥有角色">
        <div class="tag-wrap">
          <el-tag v-for="name in roleNames" :key="'role-' + name" size="small" class="area-tag">{{ name }}</el-tag>
          <span v-if="!roleNames.length" class="empty-tip">—</span>
        </div>
      </el-descriptions-item>
    </el-descriptions>

    <!-- 银行信息（整宽） -->
    <div class="section-title">银行信息</div>
    <el-descriptions :column="1" border size="small">
      <el-descriptions-item label="开户银行">{{ person.bankName || '—' }}</el-descriptions-item>
      <el-descriptions-item label="办卡姓名">{{ person.attributionName || '—' }}</el-descriptions-item>
      <el-descriptions-item label="办卡电话">{{ person.bankPhone || '—' }}</el-descriptions-item>
      <el-descriptions-item label="银行卡号">{{ person.bankCardNumbers || '—' }}</el-descriptions-item>
      <el-descriptions-item label="支行名称">{{ person.branch || '—' }}</el-descriptions-item>
    </el-descriptions>

    <!-- 关联银行卡（整宽） -->
    <div v-for="(bank, index) in partnerBanks" :key="'bank-' + index" class="partner-bank">
      <div class="partner-bank-title">关联银行卡 {{ index + 1 }}</div>
      <el-descriptions :column="1" border size="small">
        <el-descriptions-item label="开户银行">{{ bank.bankName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="银行卡号">{{ bank.bankCardNumbers || '—' }}</el-descriptions-item>
        <el-descriptions-item label="绑定姓名">{{ bank.attributionName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="绑定电话">{{ bank.bankPhone || '—' }}</el-descriptions-item>
        <el-descriptions-item label="绑定身份证">{{ bank.identity || '—' }}</el-descriptions-item>
      </el-descriptions>
    </div>

    <!-- 个人基础信息 | 人事与合同（左右两栏） -->
    <div class="two-col">
      <div class="two-col-item">
        <div class="section-title">个人基础信息</div>
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="姓名">{{ displayName }}</el-descriptions-item>
          <el-descriptions-item label="性别">{{ sexText || '—' }}</el-descriptions-item>
          <el-descriptions-item label="民族">{{ person.nationName || '—' }}</el-descriptions-item>
          <el-descriptions-item label="婚姻状态">{{ marriageText }}</el-descriptions-item>
          <el-descriptions-item label="生日">{{ birthdayText }}</el-descriptions-item>
          <el-descriptions-item label="身份证号">{{ person.identity || '—' }}</el-descriptions-item>
          <el-descriptions-item label="个人电话">{{ person.personagePhone || '—' }}</el-descriptions-item>
          <el-descriptions-item label="个人邮箱">{{ person.mailbox || '—' }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <div class="two-col-item">
        <div class="section-title">人事与合同</div>
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="合同类型">{{ hrContractType }}</el-descriptions-item>
          <el-descriptions-item label="员工状态">{{ hrStaffType }}</el-descriptions-item>
          <el-descriptions-item label="工龄">{{ hrYearsOfWorking }}</el-descriptions-item>
          <el-descriptions-item label="司龄">{{ hrCompanyAge }}</el-descriptions-item>
          <el-descriptions-item label="首次工作日期">{{ hrFirstWorkDate }}</el-descriptions-item>
          <el-descriptions-item label="入职日期">{{ hrEntryDate }}</el-descriptions-item>
          <el-descriptions-item label="工作地点">{{ hrWorkplaceName }}</el-descriptions-item>
          <el-descriptions-item label="合同签署主体">{{ hrSignFirstParty }}</el-descriptions-item>
          <el-descriptions-item label="合同开始日期">{{ hrContractStart }}</el-descriptions-item>
          <el-descriptions-item label="合同结束日期">{{ hrContractEnd }}</el-descriptions-item>
        </el-descriptions>

        <!-- 转正信息 -->
        <template v-if="officialApply">
          <div class="apply-box">
            <div class="apply-box-title">转正信息</div>
            <div>转正申请时间：{{ officialApply.createTime || '—' }}</div>
            <div>转正时间：{{ officialApply.officialDate || '—' }}</div>
          </div>
        </template>
        <template v-else-if="officialList.length">
          <div class="section-subtitle">转正记录（共 {{ officialList.length }} 条）</div>
          <div v-for="(o, i) in officialList" :key="'off-' + i" class="apply-box">
            <div>申请时间：{{ o.applyDate || '—' }}</div>
            <div>转正时间：{{ o.officialDate || '—' }}</div>
          </div>
        </template>

        <!-- 合同列表(花名册详情携带) -->
        <template v-if="contracts.length">
          <div class="section-subtitle">合同列表（共 {{ contracts.length }} 条）</div>
          <div
            v-for="(contract, i) in contracts"
            :key="'ct-' + i"
            class="contract-card"
            :class="contract.contractNumType == 1 ? 'contract-ok' : 'contract-bad'"
          >
            <div class="contract-title">{{ contract.signContractFirstPartyName || '—' }}</div>
            <div class="contract-date">
              <span>合同类型：{{ contract.contractTypeName || '—' }}</span>
              <span>有效日期：{{ contract.contractStartTime || '—' }} - {{ contract.contractEndTime || '—' }}</span>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 联系与住址 | 教育经历（左右两栏） -->
    <div class="two-col">
      <div class="two-col-item">
        <div class="section-title">联系与住址</div>
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="紧急联系人">{{ person.emergencyContact || '—' }}</el-descriptions-item>
          <el-descriptions-item label="联系人关系">{{ person.emergencyContactRelation || '—' }}</el-descriptions-item>
          <el-descriptions-item label="联系人电话">{{ person.emergencyContactPhone || '—' }}</el-descriptions-item>
          <el-descriptions-item label="现住地址">{{ formatAddr(person.thisAddress) }}</el-descriptions-item>
          <el-descriptions-item label="现住详细地址">{{ person.thisDetailedAddress || '—' }}</el-descriptions-item>
          <el-descriptions-item label="户籍地址">{{ formatAddr(person.registeredResidenceSite) }}</el-descriptions-item>
          <el-descriptions-item label="户籍详细地址">{{ person.registeredResidenceDetailedAddress || '—' }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <div class="two-col-item">
        <div class="section-title">教育经历</div>
        <template v-if="educations.length > 0">
          <el-descriptions
            v-for="(education, index) in educations"
            :key="'edu-' + index"
            :column="2"
            border
            size="small"
            class="education-item"
          >
            <el-descriptions-item label="毕业院校">{{ education.schoolName || '—' }}</el-descriptions-item>
            <el-descriptions-item label="专业">{{ education.major || '—' }}</el-descriptions-item>
            <el-descriptions-item label="学历">{{ education.educationRankIdName || '—' }}</el-descriptions-item>
            <el-descriptions-item label="毕业时间">{{ education.graduateTime || '—' }}</el-descriptions-item>
            <el-descriptions-item label="学历类型">{{ education.isFullTimeName || '—' }}</el-descriptions-item>
          </el-descriptions>
        </template>
        <div v-else class="empty-tip">暂无教育经历</div>
      </div>
    </div>

    <!-- 签字结果（整宽） -->
    <div class="section-title">签字结果</div>
    <el-descriptions :column="1" border size="small">
      <el-descriptions-item label="当前已确认签字">
        <div class="sign-img-wrap">
          <el-image
            v-if="signUrl"
            :src="signUrl"
            fit="contain"
            class="sign-img"
            :preview-src-list="[signUrl]"
            preview-teleported
          >
            <template #error>
              <span class="img-tip">图片加载失败</span>
            </template>
          </el-image>
          <span v-else class="empty-tip">暂无签字</span>
        </div>
      </el-descriptions-item>
      <el-descriptions-item label="签字时间">{{ signTime || '—' }}</el-descriptions-item>
    </el-descriptions>

    <!-- 页脚 -->
    <div class="page-footer">
      <div class="footer-left">打印时间：{{ printTime }}</div>
      <div class="footer-right">本人签字：____________</div>
    </div>
    <button class="print-btn" @click="handlePrint">点击打印</button>
  </div>
</template>

<script>
export default {
  name: "PrintPreView",
  data() {
    return {
      formData: {},
      printTime: ''
    }
  },
  created() {
    try {
      this.formData = JSON.parse(this.$route.params.row) || {};
    } catch (e) {
      console.error('[PrintPreView] 解析路由参数失败:', e);
      this.formData = {};
    }
    // 生成打印时间
    const now = new Date();
    this.printTime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  },
  computed: {
    // 展示姓名(兼容 coachName 与 coachList[0].coachname)
    displayName() {
      if (this.formData.coachName) return this.formData.coachName;
      const cl = this.formData.coachList;
      if (Array.isArray(cl) && cl.length) return cl[0].coachname;
      return '—';
    },
    // 工号
    workNumber() {
      return this.formData.workNumber || '—';
    },
    // 部门/校区(兼容顶层字段、coachRoster、coachList[0])
    deptName() {
      const cl = Array.isArray(this.formData.coachList) ? this.formData.coachList[0] : null;
      return this.formData.deptName
        || (this.formData.coachRoster && this.formData.coachRoster.deptName)
        || (cl && cl.deptName)
        || '—';
    },
    campusName() {
      return this.formData.campusName || (this.formData.coachRoster && this.formData.coachRoster.campusName) || '';
    },
    // 个人/人事详情对象(兼容 coachPersonageDetails 与 coachDetails 两套命名)
    person() {
      return this.formData.coachPersonageDetails || this.formData.coachDetails || {};
    },
    // 性别文案
    sexText() {
      const sex = this.person.sex;
      if (sex === 0) return '女';
      if (sex === 1) return '男';
      return '';
    },
    // 婚姻状态文案
    marriageText() {
      const m = this.person.isMarriage;
      if (m === 0) return '未婚';
      if (m === 1) return '已婚';
      return '—';
    },
    // 生日文案
    birthdayText() {
      const type = this.person.birthdayType;
      const bd = this.person.birthday;
      if (!bd) return '—';
      return (type === 0 ? '阴历' : '阳历') + ' - ' + bd;
    },
    // 关联银行卡
    partnerBanks() {
      return Array.isArray(this.formData.partnerBanks) ? this.formData.partnerBanks : [];
    },
    // 教育经历(兼容 coachEducationList 与 educationList)
    educations() {
      return this.formData.coachEducationList || this.formData.educationList || [];
    },
    // 合同列表(花名册详情携带)
    contracts() {
      return Array.isArray(this.formData.contractList) ? this.formData.contractList : [];
    },
    // 转正信息(教练管理详情为单对象)
    officialApply() {
      return this.formData.coachOfficialApply || null;
    },
    officialList() {
      return Array.isArray(this.formData.officialList) ? this.formData.officialList : [];
    },
    // 角色名称列表
    roleNames() {
      if (!Array.isArray(this.formData.roles)) return [];
      return this.formData.roles.map(r => r && r.name).filter(Boolean);
    },
    // 人事与合同: 合同类型
    hrContractType() {
      const r = this.formData.coachRoster;
      return (r && r.contractTypeName) || '—';
    },
    hrStaffType() {
      const r = this.formData.coachRoster;
      return (r && r.staffTypeName) || this.formData.staffTypeName || '—';
    },
    hrYearsOfWorking() {
      const r = this.formData.coachRoster;
      const v = r && r.yearsOfWorking != null ? r.yearsOfWorking : this.formData.yearsOfWorking;
      return v != null ? v : '—';
    },
    hrCompanyAge() {
      const r = this.formData.coachRoster;
      const v = r && r.companyAge != null ? r.companyAge : this.formData.companyAge;
      return v != null ? v : '—';
    },
    hrFirstWorkDate() {
      const r = this.formData.coachRoster;
      return (r && r.theFirstWorkDate) || this.formData.theFirstWorkDate || '—';
    },
    hrEntryDate() {
      const r = this.formData.coachRoster;
      return (r && r.entryDate) || this.formData.entryDate || '—';
    },
    hrWorkplaceName() {
      const r = this.formData.coachRoster;
      return (r && r.workplaceName) || this.formData.workplaceName || '—';
    },
    hrSignFirstParty() {
      const r = this.formData.coachRoster;
      return (r && r.signContractFirstPartyName) || '—';
    },
    hrContractStart() {
      const r = this.formData.coachRoster;
      return (r && r.contractStartTime) || '—';
    },
    hrContractEnd() {
      const r = this.formData.coachRoster;
      return (r && r.contractEndTime) || '—';
    },
    // 签字图
    signUrl() {
      const r = this.formData.coachRoster;
      if (r && r.signUrl) return r.signUrl;
      if (this.formData.signReview && this.formData.signReview.signUrl) return this.formData.signReview.signUrl;
      return '';
    },
    signTime() {
      const r = this.formData.coachRoster;
      return (r && r.signTime) || '';
    }
  },
  methods: {
    handlePrint() {
      window.print();
    },
    // 数组地址转字符串
    formatAddr(val) {
      if (Array.isArray(val)) return val.join('');
      return val || '—';
    }
  }
}
</script>

<style scoped>
/* A4 纸张尺寸：210mm × 297mm */
.a4-page {
  width: 210mm;
  min-height: 297mm;
  padding: 8mm 10mm;
  margin: 20px auto;
  background: #fff;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  font-family: "Microsoft YaHei", "SimSun", sans-serif;
  color: #333;
  font-size: 12px;
}

/* 顶部标题 */
.page-header {
  text-align: center;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 2px solid #409eff;
}

.page-header h2 {
  margin: 0 0 4px 0;
  font-size: 22px;
  font-weight: bold;
  color: #1f2d3d;
  letter-spacing: 2px;
}

/* 顶部概要 */
.detail-summary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 14px;
  background: #f0f7ff;
  border: 1px solid #e4ecf7;
  border-radius: 20px;
}

.summary-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

/* 区块标题 */
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
  margin: 14px 0 6px 0;
  padding: 4px 10px;
  background: linear-gradient(90deg, #ecf5ff 0%, #ffffff 100%);
  border-left: 4px solid #409eff;
  border-radius: 4px;
}

/* 子标题(转正记录/合同列表等从属区块) */
.section-subtitle {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin: 10px 0 4px 2px;
}

.info-section {
  margin-bottom: 4px;
}

/* 左右两栏（个人基础信息|人事与合同、联系与住址|教育经历） */
.two-col {
  display: flex;
  gap: 16px;
  flex-wrap: nowrap;
  margin-top: 6px;
}

.two-col-item {
  flex: 1 1 0;
  min-width: 0;
}

/* 描述列表美化：标签列浅底 */
.a4-page ::v-deep .el-descriptions__label {
  width: 120px;
  background: #f5f7fa;
  color: #606266;
  font-weight: 600;
  text-align: right;
  padding-right: 12px;
}

/* 半栏内标签列收窄，避免挤压值 */
.two-col-item ::v-deep .el-descriptions__label {
  width: 86px;
  padding-right: 8px;
}

.a4-page ::v-deep .el-descriptions__content {
  color: #303133;
  background: #fff;
}

.a4-page ::v-deep .el-descriptions--small td {
  padding: 4px 10px;
}

.img-tip {
  color: #909399;
  font-size: 12px;
}

.empty-tip {
  color: #909399;
  font-size: 12px;
}

/* 关联银行卡卡片 */
.partner-bank {
  background: #f7f9fc;
  border: 1px solid #e8edf3;
  border-radius: 8px;
  padding: 8px 12px 4px;
  margin-top: 10px;
}

.partner-bank-title {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 6px;
}

/* 转正信息 */
.apply-box {
  background: #fdf6ec;
  border: 1px solid #f5e4c5;
  border-radius: 8px;
  padding: 8px 14px;
  margin-top: 10px;
  color: #606266;
  font-size: 13px;
  line-height: 1.9;
}

.apply-box-title {
  font-weight: 600;
  color: #b88230;
  margin-bottom: 4px;
}

/* 合同卡片 */
.contract-card {
  border-radius: 8px;
  padding: 8px 14px;
  margin-top: 10px;
  font-size: 13px;
  line-height: 1.8;
}

.contract-ok {
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
}

.contract-bad {
  background: #f4f4f5;
  border: 1px solid #e9e9eb;
}

.contract-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.contract-date {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
}

/* 教育经历 */
.education-item {
  margin-bottom: 8px;
}

/* 标签 */
.tag-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.area-tag {
  margin: 2px 0;
}

/* 签字图：宽度双倍、保持比例不拉伸 */
.sign-img-wrap {
  display: inline-block;
  padding: 8px;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
}

.sign-img {
  width: 288px;
  height: 150px;
  display: block;
}

.page-footer {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
  padding-left: 4px;
  padding-right: 4px;
}

/* 打印按钮 */
.print-btn {
  align-self: center;
  margin-top: 18px;
  padding: 8px 28px;
  font-size: 14px;
  color: #fff;
  background: #409eff;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.35);
}

.print-btn:hover {
  background: #66b1ff;
}

/* 打印样式 */
@media print {
  .a4-page {
    width: 100%;
    min-height: auto;
    padding: 0;
    margin: 0;
    box-shadow: none;
  }

  .print-btn {
    display: none !important;
  }

  @page {
    size: A4;
    margin: 6mm;
  }
}
</style>

<!-- 全局打印样式：隐藏后台框架、复位内容容器偏移，使打印区域铺满页面宽度；
     采用正常文档流（而非 absolute）布局，支持长内容多页连续打印，
     且消除此前“左侧约 250px（侧边栏宽度）大片空白”的问题。 -->
<style>
@media print {
  /* 通用兜底：先隐藏所有元素 */
  body * {
    visibility: hidden !important;
  }
  /* 仅显示打印区域及其内容 */
  #printArea,
  #printArea * {
    visibility: visible !important;
  }

  /* 隐藏后台框架（侧边栏/顶栏/菜单/标签等），避免把导航也印出来 */
  .sidebar,
  .navbar,
  .header,
  .tags,
  .tags-view,
  aside,
  nav,
  .el-aside,
  .el-header,
  .el-menu,
  .crumbs {
    display: none !important;
  }

  /* 复位内容容器与根容器：
     1) 消除 .content-box 的 left:250 偏移与内边距（左侧大片空白的根因）；
     2) 关键：main.css 里全局设了 html/body/#app/.wrapper { overflow:hidden; height:100% }。
        若不解除，static 文档流下的长内容会被 #app 裁切为“只打印一页”，必须改为
        overflow:visible + height:auto，让内容高度由实际内容撑开、自动分页（多页连续打印）。 */
  html,
  body,
  #app,
  .wrapper,
  .content-box,
  .content-box1,
  .content,
  .container,
  .app-main,
  .main-container {
    position: static !important;
    left: auto !important;
    right: auto !important;
    top: auto !important;
    bottom: auto !important;
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    background: #fff !important;
  }

  /* 打印区域回归正常文档流并铺满可用宽度（不再用 absolute 导致相对偏移容器定位） */
  #printArea {
    position: static !important;
    left: auto !important;
    top: auto !important;
    right: auto !important;
    width: 100% !important;
    min-height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    background: #fff !important;
  }
}
</style>
